import { Card, Row, Col, Typography, Button } from "antd";
import { useTranslation } from "react-i18next";
import { InstanceStatus } from "./InstanceStatus";
import { InstanceVersion } from "./InstanceVersion";
import { InstancePool } from "./InstancePool";
import { UsedInstances } from "./UsedInstances";
import { Link } from "react-router-dom";
import { CanAccess, useUpdate, useInvalidate } from "@refinedev/core";
import { useState, useEffect } from "react";
import { useFormatter } from "@/hooks/useFormatter";

const { Text } = Typography;

export const InstanceInfo = ({ data, resource }) => {
  const keysOrder = [
    "id",
    // "name",
    "type",
    // "status",
    "busyOnStep",
    "version",
    "platform",
    "created_by",
    "created_on",
    "tenant",
    "domain",
    "subdomain",
    "url",
    "hadoopURL",
    "sparkURL",
    "dashboardURL",
    "druidURL",
    "neo4jURL",
    "connectorUrls",
    "syslogURL",
    "snmptrapURL",
    "pool",
    "used_instances",
    "allowedActions",
  ];

  const [status, setStatus] = useState("");
  const [isRetrying, setIsRetrying] = useState(false);
  const invalidate = useInvalidate();

  useEffect(() => {
    setStatus(data?.value?.status);
  }, [data]);

  const { mutate: retryCreate } = useUpdate({
    resource,
    successNotification: (data1, values, resource) => {
      setIsRetrying(false);
      invalidate({
        resource,
        invalidates: ["list"],
      });

      const s = data1?.status;
      setStatus(s);
      return {
        message: t(`${resource}.actionResult.retry.success`, {
          id: values?.id,
        }),
        type: "success",
      };
    },
    errorNotification: (data1, values, resource) => {
      setIsRetrying(false);
      return {
        message:
          t(`${resource}.actionResult.retry.failed`, {
            id: values?.id,
          }) +
          "--" +
          data1?.message,
        type: "error",
      };
    },
  });

  const handleUrlClick = (e, name) => {
    e.preventDefault();
    window.open(`/api/v1/provision/jump/${name}`, "_blank");
  };

  const handleNormalUrlClick = (e, url) => {
    e.preventDefault();
    window.open(`${url}`, "_blank");
  };

  const handleRetryClick = (e, id) => {
    e.preventDefault();
    setIsRetrying(true);
    retryCreate({
      id,
      values: { action: "retry" },
    });
  };

  const { t } = useTranslation();
  const { formatDateTime } = useFormatter();

  return (
    <Card
      title={
        data?.value?.name && (
          <>
            <span style={{ paddingRight: 20 }}>{data?.value?.name}</span>
            <InstanceStatus status={status} />
            <span style={{ paddingLeft: "10px" }}></span>
            {status?.indexOf("failed") != -1 && (
              <CanAccess resource={resource} action="retry">
                <Button
                  style={{ paddingLeft: "10px" }}
                  to="retry"
                  loading={isRetrying}
                  onClick={(e) => handleRetryClick(e, data?.value?.id)}
                >
                  {t("actions.retry", "Retry")}
                </Button>
              </CanAccess>
            )}
          </>
        )
      }
      style={{ width: 858 }}
    >
      {data.status === "error" || data.status === "Error" ? (
        <p>
          <Text strong>{t("instances.status.error")}</Text>
          {data.value ? data.value : data.error.message}
        </p>
      ) : data.status == "nonExist" ? (
        <p>{t("instances.status.nonExist")}</p>
      ) : data.status === "OK" ? (
        keysOrder.map((key) => {
          if (data.value.hasOwnProperty(key)) {
            const value = data.value[key];
            return (
              <Row key={key} style={{ marginBottom: 8 }} align="middle">
                <Col span={6}>
                  <Text strong text-align="top">{t(`instances.fields.${key}`, key)}:</Text>
                </Col>
                <Col span={18}>
                  <Text>
                    {key === "status" ? (
                      <InstanceStatus status={value} />
                    ) : key === "pool" ? (
                      <InstancePool pool={value} />
                    ) : key === "allowedActions" ? (
                      value.join(",  ")
                    ) : key === "url" ? (
                      <Link
                        to={value}
                        onClick={(e) => handleUrlClick(e, data.value.subdomain)}
                      >
                        {value}
                      </Link>
                    ) : key === "hadoopURL" ? (
                      <Link
                        to={value}
                        onClick={(e) =>
                          handleNormalUrlClick(e, data.value.hadoopURL)
                        }
                      >
                        {value}
                      </Link>
                    ) : key === "sparkURL" ? (
                      <Link
                        to={value}
                        onClick={(e) =>
                          handleNormalUrlClick(e, data.value.sparkURL)
                        }
                      >
                        {value}
                      </Link>
                    ) : key === "dashboardURL" ? (
                      <Link
                        to={value}
                        onClick={(e) =>
                          handleNormalUrlClick(e, data.value.dashboardURL)
                        }
                      >
                        {value}
                      </Link>
                    ) : key === "druidURL" ? (
                      <Link
                        to={value}
                        onClick={(e) =>
                          handleNormalUrlClick(e, data.value.druidURL)
                        }
                      >
                        {value}
                      </Link>
                    ) : key === "neo4jURL" ? (
                      <Link
                        to={value}
                        onClick={(e) =>
                          handleNormalUrlClick(e, data.value.neo4jURL)
                        }
                      >
                        {value}
                      </Link>
                    ) : key === "connectorUrls" ? (
                      <>
                       {value?.kubernetes && (
                        <Row style={{ marginBottom: 1 }} align="left">
                          <Col span={4}> <Text>{t(`instances.connector.kubernetes`)}:</Text> </Col>
                          <Col>
                            <Link to={value.kubernetes} onClick={(e) => handleNormalUrlClick(e, value.kubernetes) }>
                              {value.kubernetes}
                            </Link>
                          </Col>
                        </Row>
                        )}

                        {value?.logs && (
                        <Row style={{ marginBottom: 1 }} align="left">
                          <Col span={4}> <Text>{t(`instances.connector.logs`)}:</Text> </Col>
                          <Col>
                            <Link to={value.logs} onClick={(e) => handleNormalUrlClick(e, value.logs) }>
                              {value.logs}
                            </Link>
                          </Col>
                        </Row>
                        )}

                        {value?.snmptrap && (
                        <Row style={{ marginBottom: 1 }} align="left">
                          <Col span={4}> <Text>{t(`instances.connector.snmptrap`)}:</Text> </Col>
                          <Col>
                            <Link to={value.snmptrap} onClick={(e) => handleNormalUrlClick(e, value.snmptrap) }>
                              {value.snmptrap}
                            </Link>
                          </Col>
                        </Row>
                        )}
                      </>
                    ) : key === "version" ? (
                      <InstanceVersion
                        ver={value}
                        platform={data.value.platform}
                        id={data?.value?.id}
                        name={data?.value?.name}
                        setStatus={setStatus}
                        allowedActions={data?.value?.allowedActions || []}
                      ></InstanceVersion>
                    ) : key === "used_instances" ? (
                      <UsedInstances instances={value}></UsedInstances>
                    ) : key === "created_on" ? (
                      formatDateTime(value) && (
                        <Typography.Text copyable>{formatDateTime(value)}</Typography.Text>
                      )
                    ) : (
                      value && (
                        <Typography.Text copyable>{value}</Typography.Text>
                      )
                    )}
                  </Text>
                </Col>
              </Row>
            );
          }
          return null;
        })
      ) : (
        <></>
      )}
    </Card>
  );
};
